import { useEffect, useRef, useState } from 'react';

import { useTranslate } from '../../../../../../hooks/useTranslate';
import { CloseButton } from '../../../../../Button/components/CloseButton';
import { LoadingDots } from '../../../../../LoadingDots/LoadingDots';
import { useAnalytics, useApi } from './AmeliaV2.hooks';
import {
  Chat,
  DotsWrapper,
  Footer,
  Header,
  StyledBox,
  Text,
} from './AmeliaV2.styles';
import { Form } from './components/Form/Form';
import type { FormInput } from './components/Form/Form.types';
import { Message } from './components/Message/Message';
import { Author, Avatar } from './components/Message/Message.styles';
import type { Message as MessageType } from './components/Message/Message.types';

type Props = {
  onClose: () => void;
  initialPrompt?: string;
};

export const AmeliaV2 = ({ onClose, initialPrompt }: Props) => {
  const { t } = useTranslate();
  const { onInputInitiated, onItineraryReceived, onViewTripClicked } =
    useAnalytics();
  const [messages, setMessages] = useState<MessageType[]>([
    {
      author: 'amelia',
      payload: {
        type: 'initial',
      },
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const { sendMessage } = useApi();

  useEffect(() => {
    lastMessageRef.current?.scrollIntoView();
  }, [messages]);

  const onSubmit = async ({ prompt }: FormInput) => {
    setIsLoading(true);

    setMessages((prev) => [
      ...prev,
      {
        author: 'user',
        payload: {
          type: 'plain',
          content: prompt,
        },
      },
    ]);

    const { isServerError, itinerary, plain } = await sendMessage(prompt);
    let payload: MessageType['payload'];

    if (isServerError) {
      payload = {
        type: 'server-error',
        prompt,
      };
    } else if (itinerary) {
      payload = {
        type: 'itinerary',
        prompt,
        content: itinerary,
      };
      onItineraryReceived(prompt, itinerary);
    } else if (plain) {
      payload = {
        type: 'plain',
        content: plain,
      };
    } else {
      payload = {
        type: 'plain',
        content: t('explore.amelia.unhandledMessage'),
      };
    }

    setMessages((prev) => [
      ...prev,
      {
        author: 'amelia',
        payload,
      },
    ]);
    setIsLoading(false);
  };

  const tryAgain = async (prompt: string) => {
    await onSubmit({ prompt });
  };

  return (
    <StyledBox>
      <Header>
        <Text>{t('explore.amelia.header')}</Text>
        <CloseButton onClick={onClose} />
      </Header>

      <Chat>
        {messages.map(({ author, payload }, i) => (
          <Message
            key={i}
            ref={i === messages.length - 1 ? lastMessageRef : null}
            author={author}
            payload={payload}
            onTryAgainClicked={tryAgain}
            onViewTripClicked={onViewTripClicked}
          />
        ))}

        {isLoading && (
          <>
            <Author>
              <Avatar src="/m/amelia-avatar@2x.png" alt="Amelia avatar" />

              {t(`explore.amelia.author.amelia`)}
            </Author>
            <DotsWrapper>
              <LoadingDots height="10" />
            </DotsWrapper>
          </>
        )}
      </Chat>

      <Footer>
        <Form
          isLoading={isLoading}
          onInputInitiated={onInputInitiated}
          initialPrompt={initialPrompt}
          onSubmit={onSubmit}
        />
      </Footer>
    </StyledBox>
  );
};
